import type { Breakpoint } from '../../platform/responsive/useBreakpoint'
import type { ButtonHTMLAttributes, DragEvent, ReactNode } from 'react'
import { ArrowDown, ArrowUp, Pencil, Trash2 } from 'lucide-react'
import { Button } from '../../platform/primitives/Button'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import type { ReadRule, RuleDraft } from './ruleWire'
import { rulePath } from './rulePaths'
import { ConditionSentence, WhenSentence } from './RuleSentence'
import { actionText, whenText, type Names } from './ruleText'

/** What "Applied N" counts. One function so the source can change in one place —
 *  the owner chose `times_applied` for now and may swap it later. */
export function appliedCount(rule: ReadRule['rule']): number {
  return rule.times_applied
}

/** The words a filter searches, and what a row shows under its name. Read-only
 *  rules have no draft, so their summary is the fixed explanation instead. */
export const READ_ONLY_NOTE = "Made on another device with conditions this screen can't edit yet."

/** An amount condition saved with no direction (#517) compares the signed
 *  amount. Editing it here would save it with a direction and quietly change
 *  what it matches, so it is read-only — and says so, without promising more. */
export const LEGACY_AMOUNT_NOTE =
  'Made on another device. Its amount condition keeps the meaning it was saved with, so it can’t be edited here.'

/** Why a read-only rule cannot be opened, in the words its row shows. */
export function readOnlyNote(read: Extract<ReadRule, { editable: false }>): string {
  return read.reason === 'legacy_amount' ? LEGACY_AMOUNT_NOTE : READ_ONLY_NOTE
}

export function summaryOf(read: ReadRule, names: Names): string {
  if (!read.editable) return readOnlyNote(read)
  return [whenText(read.draft, names), ...read.draft.actions.map((a) => actionText(a, names))].join(' ')
}

/** Reorder controls for one row. Absent while a filter is active. */
export type RowOrder = {
  isFirst: boolean
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  /** Present on desktop only; the buttons remain the accessible path. */
  drag?: {
    dragging: boolean
    /** The edge the dragged row will land against; null when this row is not the target. */
    over: 'above' | 'below' | null
    onDragStart: (e: DragEvent<HTMLLIElement>) => void
    onDragOver: (e: DragEvent<HTMLLIElement>) => void
    onDrop: (e: DragEvent<HTMLLIElement>) => void
    onDragEnd: () => void
  }
}

/** How a row is laid out. From `useBreakpoint()`, never a media query (M11-R23).
 *  - `wide`: (desktop and up, 1024px) the design's seven columns, actions in their own THEN column.
 *  - `mid`: the THEN column folds under the conditions.
 *  - `phone`: the stacked card. */
export type RowLayout = 'wide' | 'mid' | 'phone'

/** The one mapping from `useBreakpoint()` to a row layout. The seven columns
 *  start at desktop (1024px); below it THEN folds under the conditions, and
 *  below 640px the row is a card. */
export function rowLayoutFor(breakpoint: Breakpoint): RowLayout {
  if (breakpoint === 'phone') return 'phone'
  return breakpoint === 'desktop' || breakpoint === 'wide' ? 'wide' : 'mid'
}

/** Edit and Delete: a round, icon- or word-sized control in a row. The skin is `rule-row__icon`
 *  in rules.css; only the floor is inline, so a caller cannot shrink it and it
 *  lifts to 44px below 640px with every other control (M0-R29), on both axes. */
function RowButton({
  tone,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'neutral' | 'accent' | 'danger'; children: ReactNode }) {
  // `page` (33px), not `panel` (30px): a control that carries no `.btn` size
  // class claims the page floor, and the hit-target sweep holds it to that.
  const target = useHitTarget('page')
  return (
    <button
      type="button"
      className={['rule-row__icon', `rule-row__icon--${tone ?? 'neutral'}`, className].filter(Boolean).join(' ')}
      style={{ minHeight: target, minWidth: target }}
      {...rest}
    >
      {children}
    </button>
  )
}

/** The conditions as the design draws them: one chip each, the group's join
 *  word between chips, and "OR" between groups (groups are always OR'd). */
function ConditionChips({ draft, names }: { draft: RuleDraft; names: Names }) {
  const many = draft.groups.length > 1
  return (
    <div className="rule-row__conds">
      {draft.groups.map((g, gi) => (
        // Groups and conditions have no ids; their order is their identity.
        <span key={`group-${gi}`} className="rule-row__group-wrap">
          {gi > 0 && <span className="rule-row__lead">OR</span>}
          <span className={`rule-row__group${many ? ' rule-row__group--boxed' : ''}`}>
            {g.conditions.map((c, ci) => (
              <span key={`${c.field}-${ci}`} className="rule-row__cond-wrap">
                {ci > 0 && <span className="rule-row__join">{g.join}</span>}
                <span className="rule-row__cond">
                  <ConditionSentence cond={c} names={names} />
                </span>
              </span>
            ))}
          </span>
        </span>
      ))}
    </div>
  )
}

export function RuleRow({
  read,
  position,
  names,
  onEdit,
  onToggle,
  onDelete,
  order,
  switching = false,
  layout = 'mid',
}: {
  read: ReadRule
  /** The rule's real priority rank — never renumbered by a filter. */
  position: number
  names: Names
  onEdit: () => void
  onToggle: () => void
  onDelete: () => void
  order?: RowOrder
  /** Its PATCH or dry run is in flight: the switch refuses a second press. */
  switching?: boolean
  layout?: RowLayout
}) {
  const { rule } = read
  const drag = order?.drag
  const phone = layout === 'phone'
  const paused = !rule.is_enabled
  const toggle = (
    <button
      type="button"
      role="switch"
      aria-checked={rule.is_enabled}
      aria-label={`Enable ${rule.name}`}
      aria-disabled={switching || undefined}
      className={`rule-row__switch${rule.is_enabled ? ' rule-row__switch--on' : ''}`}
      onClick={switching ? undefined : onToggle}
    >
      <span className="rule-row__switch-track" aria-hidden="true">
        <span className="rule-row__switch-thumb" />
      </span>
    </button>
  )
  const glyph = phone ? 15 : 14
  const moveButtons = order !== undefined && (
    <>
      {/* `Button`, not RowButton: the first row's ↑ and the last row's ↓ are
          aria-disabled, and the disabled skin belongs to primitives.css. */}
      <Button
        variant="chip"
        size="panel"
        className="rule-row__move"
        aria-label={`Move ${rule.name} up`}
        aria-disabled={order.isFirst}
        onClick={order.isFirst ? undefined : order.onMoveUp}
      >
        <ArrowUp size={glyph} aria-hidden="true" />
      </Button>
      <Button
        variant="chip"
        size="panel"
        className="rule-row__move"
        aria-label={`Move ${rule.name} down`}
        aria-disabled={order.isLast}
        onClick={order.isLast ? undefined : order.onMoveDown}
      >
        <ArrowDown size={glyph} aria-hidden="true" />
      </Button>
    </>
  )
  const actionChips = read.editable && (
    <div className="rule-row__actions">
      {layout === 'mid' && <span className="rule-row__then">Then</span>}
      {read.draft.actions.map((a, i) => (
        <span key={`${a.type}-${i}`} className="rule-row__action" title={actionText(a, names)}>
          {actionText(a, names)}
        </span>
      ))}
    </div>
  )
  const state = paused ? ' rule-row--paused' : ''
  // The name opens the rule's page; Edit still opens the builder.
  const nameLink = (
    <InlineLink to={rulePath(rule.id)} className="rule-row__name-link">
      <span className="rule-row__name">{rule.name}</span>
    </InlineLink>
  )
  if (phone) {
    // The design's phone card: order, name, switch; the summary; the actions;
    // then one row of "Applied N · paused" with ↑ ↓ Edit. No drag on a phone.
    // Delete lives in the builder — except for a read-only rule, which has no
    // builder to open and so keeps Delete here (R21).
    return (
      <li className={`rule-row rule-row--phone${state}`}>
        <div className="rule-row__top">
          <span className="rule-row__pos">{position}</span>
          {nameLink}
          {toggle}
        </div>
        <p className="rule-row__when">
          {read.editable ? <WhenSentence draft={read.draft} names={names} /> : readOnlyNote(read)}
        </p>
        {actionChips}
        <div className="rule-row__meta">
          <span className="rule-row__applied">{`Applied ${appliedCount(rule)}${paused ? ' · paused' : ''}`}</span>
          {moveButtons}
          {read.editable ? (
            <RowButton tone="accent" className="rule-row__word" onClick={onEdit} aria-label={`Edit ${rule.name}`}>
              Edit
            </RowButton>
          ) : (
            <RowButton tone="danger" className="rule-row__word" onClick={onDelete} aria-label={`Delete ${rule.name}`}>
              Delete
            </RowButton>
          )}
        </div>
      </li>
    )
  }
  return (
    <li
      className={`rule-row rule-row--${layout}${state}${drag?.dragging ? ' rule-row--dragging' : ''}${drag?.over ? ` rule-row--over-${drag.over}` : ''}`}
      draggable={drag !== undefined ? true : undefined}
      onDragStart={drag?.onDragStart}
      onDragOver={drag?.onDragOver}
      onDrop={drag?.onDrop}
      onDragEnd={drag?.onDragEnd}
    >
      {drag !== undefined && (
        <span className="rule-row__grip" aria-hidden="true">
          ⋮⋮
        </span>
      )}
      <span className="rule-row__pos">{position}</span>
      <div className="rule-row__body">
        <div className="rule-row__head">
          {nameLink}
          {paused && <span className="rule-row__paused">Paused</span>}
        </div>
        {read.editable ? (
          <>
            <ConditionChips draft={read.draft} names={names} />
            {layout === 'mid' && actionChips}
          </>
        ) : (
          <p className="rule-row__when">{readOnlyNote(read)}</p>
        )}
      </div>
      {/* Its column header is hidden from assistive tech too, and the mid
          layout's visible "Then" is not drawn here: the cell says it. */}
      {layout === 'wide' && (
        <div className="rule-row__then-cell">
          {actionChips !== false && <span className="sr-only">Then </span>}
          {actionChips}
        </div>
      )}
      {/* The "Applied" column header is hidden from assistive tech (R18), so
          the cell names itself. */}
      <span className="rule-row__applied">
        <span className="sr-only">Applied </span>
        {appliedCount(rule)}
      </span>
      {toggle}
      {/* Four fixed tracks: ↑ ↓ edit delete. A control that is not offered
          leaves an empty cell, so the others stay in their columns. */}
      <div className="rule-row__tools">
        {moveButtons === false ? (
          <>
            <span className="rule-row__tool-gap" />
            <span className="rule-row__tool-gap" />
          </>
        ) : (
          moveButtons
        )}
        {read.editable ? (
          <RowButton onClick={onEdit} aria-label={`Edit ${rule.name}`}>
            <Pencil size={glyph} aria-hidden="true" />
          </RowButton>
        ) : (
          <span className="rule-row__tool-gap" />
        )}
        <RowButton tone="danger" onClick={onDelete} aria-label={`Delete ${rule.name}`}>
          <Trash2 size={glyph} aria-hidden="true" />
        </RowButton>
      </div>
    </li>
  )
}
